/**
 * PJ の差分の要素 1 件を、画面の行（欄の行と小見出し）へ組む。
 *
 * **業務ルールの中身は JSON のまま出さず、行に割って揃える。** 共通部の `buildPjDiff` が中身を
 * 判定表の行・式の段・並べ替えのキーの行・キーの決め方（キーごとの 形 ／ 列 ／ 行 ／ 式）へ割って
 * 返すので、ここは欄の名前（`parsePjDiffField`）で区画に分け、番号を名前の表（`getPjDiff` の
 * `labels`）で文字へ置き換えるだけにする。**どの欄が変わったか（`changed`）は比べ直さない**。
 * キーの決め方の小見出しの印（追加 ／ 削除 ／ 変更）も、その配下の欄の有無と `changed` から決める。
 *
 * 行の順は ノードの欄 → 中身の欄・行・段 → 並べ替えのキーの行 → キーの決め方（キーごと）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の要素 1 件の欄を、ノードの欄・中身・並べ替えのキー・キーの決め方の小見出しつきの行へ組み、値を名前つきの文字にする。
 * @graph-connects parsePjDiffField [calls] 欄の名前が指す場所
 * @graph-connects formatPjDiffValue [calls] 形を知らない値の文字化
 */
import { parsePjDiffField, type PjDiffFieldPath } from '@cortex/domain-spec-console';

import { formatPjDiffValue, visiblePjDiffFields, type PjDiffViewItem } from './pj-diff-view.js';

/** 差分の欄 1 つ。 @graph-connects none */
export type PjDiffViewField = PjDiffViewItem['fields'][number];

/** 番号 → 名前（`getPjDiff` の `labels`）。 @graph-connects none */
export type PjDiffLabels = ReadonlyMap<number, string>;

/** 文言を引く関数。 @graph-connects none */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * 画面の 1 行。欄の行か、キーの決め方の小見出し。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の要素 1 件の画面の行（欄の行・キーの決め方の見出し・キーごとの小見出しと印）
 * @graph-connects none
 */
export type PjDiffRow =
  | { readonly kind: 'field'; readonly field: PjDiffViewField; readonly path: PjDiffFieldPath }
  | { readonly kind: 'deriveTitle' }
  | {
      readonly kind: 'deriveKey';
      readonly key: number;
      /** キーの決め方を案件でどうしたか。変わっていなければ null */
      readonly mark: 'add' | 'update' | 'delete' | null;
    };

/** キーの決め方の中の欄か。 @graph-connects none */
function deriveKeyOf(path: PjDiffFieldPath): number | null {
  return path.part === 'derive' || path.part === 'deriveRow' || path.part === 'deriveStep'
    ? path.key
    : null;
}

/**
 * 差分の画面に出さない欄。実装 ・ 使う表 ・ 使う列は内部の実装の話、形は業務ルールの形の札で、
 * どちらも画面には出さない（Dhik 2026-09-28）。読取（`getPjDiff`）は返し続ける — MCP からは読める。
 * @graph-connects none
 */
const SCREEN_HIDDEN_FIELDS: ReadonlySet<string> = new Set([
  'implementedBy',
  'usesTable',
  'usesColumn',
  'kind',
]);

/** 差分の画面に出さない欄か（ノードの欄と業務ルールの中身の欄だけを見る）。 @graph-connects none */
function isScreenHiddenField(path: PjDiffFieldPath): boolean {
  return (path.part === 'property' || path.part === 'body') && SCREEN_HIDDEN_FIELDS.has(path.name);
}

/** 区画の順（ノードの欄 → 中身 → 並べ替えのキー）。 @graph-connects none */
function sectionRank(path: PjDiffFieldPath): number {
  if (path.part === 'property') return 0;
  return path.part === 'sortKey' ? 2 : 1;
}

/** キーの決め方 1 つの配下の欄から印を決める。 @graph-connects none */
function deriveMarkOf(fields: readonly PjDiffViewField[]): 'add' | 'update' | 'delete' | null {
  if (fields.every((field) => field.before === null)) return 'add';
  if (fields.every((field) => field.after === null)) return 'delete';
  return fields.some((field) => field.changed) ? 'update' : null;
}

/**
 * 要素 1 件の欄を画面の行へ組む。変わっていない欄を出さないときは、配下に出す欄が残らない小見出しも
 * 出さない。
 *
 * @param item 差分の要素 1 件
 * @param showUnchanged 変わっていない欄も出すか
 * @returns 画面の行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素 1 件の欄を、区画の順に並べ、キーの決め方をキーごとの小見出しと印つきで束ねた画面の行にする。
 * @graph-connects visiblePjDiffFields [calls] 出さない欄（要素の番号・元の規則）を落とす
 * @graph-connects isScreenHiddenField [calls] 画面に出さない欄（実装 ・ 使う表 ・ 使う列 ・ 形）を落とす
 * @graph-connects parsePjDiffField [calls] 欄の名前が指す場所
 * @graph-connects deriveKeyOf [calls] キーの決め方の中の欄か
 * @graph-connects sectionRank [calls] 区画の順
 * @graph-connects deriveMarkOf [calls] キーの決め方の小見出しの印
 */
export function pjDiffRowsOf(item: PjDiffViewItem, showUnchanged: boolean): PjDiffRow[] {
  const entries = visiblePjDiffFields(item, true)
    .map((field) => ({ field, path: parsePjDiffField(field.field) }))
    .filter((entry) => !isScreenHiddenField(entry.path));
  const shown = (field: PjDiffViewField) => showUnchanged || field.changed;
  const plain = entries
    .filter((entry) => deriveKeyOf(entry.path) === null)
    .map((entry, index) => ({ entry, index }))
    .sort(
      (left, right) =>
        sectionRank(left.entry.path) - sectionRank(right.entry.path) || left.index - right.index,
    )
    .map(({ entry }) => entry);
  const groups = new Map<number, (typeof entries)[number][]>();
  for (const entry of entries) {
    const key = deriveKeyOf(entry.path);
    if (key !== null) groups.set(key, [...(groups.get(key) ?? []), entry]);
  }

  const deriveRows = [...groups].flatMap(([key, group]): PjDiffRow[] => {
    const visible = group.filter((entry) => shown(entry.field));
    if (visible.length === 0) return [];
    return [
      { kind: 'deriveKey', key, mark: deriveMarkOf(group.map((entry) => entry.field)) },
      ...visible.map((entry) => ({ kind: 'field' as const, ...entry })),
    ];
  });
  return [
    ...plain
      .filter((entry) => shown(entry.field))
      .map((entry) => ({ kind: 'field' as const, ...entry })),
    ...(deriveRows.length === 0 ? [] : [{ kind: 'deriveTitle' as const }, ...deriveRows]),
  ];
}

/** 番号を名前にする。名前が引けなければ番号の綴り。 @graph-connects none */
function nameOf(value: unknown, labels: PjDiffLabels): string {
  return typeof value === 'number' ? (labels.get(value) ?? String(value)) : String(value);
}

/**
 * 欄の名前を画面の見出しにする。
 *
 * @param path 欄が指す場所
 * @param labels 番号 → 名前
 * @param t 文言
 * @returns 欄の見出し
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の欄の名前を、ノードの欄・中身の欄・行 n・式 n・並べ替え: 属性名 の見出しにする。
 * @graph-connects nameOf [calls] 並べ替えのキーの属性を名前に
 */
export function pjDiffFieldTitle(
  path: PjDiffFieldPath,
  labels: PjDiffLabels,
  t: Translate,
): string {
  switch (path.part) {
    case 'property':
      return t(`pjFieldDiff.fields.${path.name}`, { defaultValue: path.name });
    case 'body':
    case 'derive':
      return t(`pjFieldDiff.ruleFields.${path.name}`, { defaultValue: path.name });
    case 'row':
    case 'deriveRow':
      return t('pjFieldDiff.ruleRow', { n: path.rowId });
    case 'step':
    case 'deriveStep':
      return t('pjFieldDiff.ruleStep', { n: path.stepId });
    case 'sortKey':
      return t('pjFieldDiff.sortKey', { name: nameOf(path.attribute, labels) });
  }
}

/** 配列でないオブジェクトか。 @graph-connects none */
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** 文字化の材料。 @graph-connects none */
interface FormatContext {
  readonly labels: PjDiffLabels;
  readonly t: Translate;
  readonly locale: string;
  /** 要素の型（業務ルールの形・CRUD の語を引くかを決める） */
  readonly nodeType: string;
}

/**
 * 判定表のセル 1 つ（値・「—」・範囲）を文字にする。
 * @graph-connects isRecord [calls] 範囲のセルか
 * @graph-connects nameOf [calls] 番号を名前に
 */
function cellText(cell: unknown, context: FormatContext): string {
  if (cell === null) return '—';
  if (!isRecord(cell)) return nameOf(cell, context.labels);
  return Object.entries(cell)
    .map(([comparator, operand]) => {
      const target = isRecord(operand)
        ? 'literal' in operand
          ? String(operand['literal'])
          : nameOf(operand['term'], context.labels)
        : String(operand);
      return `${context.t(`businessLogic.rangeComparators.${comparator}`, { defaultValue: comparator })} ${target}`;
    })
    .join(' ・ ');
}

/**
 * 判定表の行（条件のセル → 出力）を文字にする。形が合わなければ null。
 * @graph-connects isRecord [calls] 行の形か
 * @graph-connects cellText [calls] セル 1 つ
 * @graph-connects nameOf [calls] 出力の番号を名前に
 */
function rowText(value: unknown, context: FormatContext): string | null {
  if (!isRecord(value) || !Array.isArray(value['cells'])) return null;
  const outputs = Array.isArray(value['outputs']) ? value['outputs'] : [value['output']];
  return `${value['cells'].map((cell: unknown) => cellText(cell, context)).join(' ・ ')} → ${outputs
    .map((output: unknown) => nameOf(output, context.labels))
    .join(' ・ ')}`;
}

/**
 * 式の項 1 つを文字にする。
 * @graph-connects isRecord [calls] 項と条件の形か
 * @graph-connects nameOf [calls] 番号を名前に
 */
function operandText(operand: unknown, context: FormatContext): string {
  if (!isRecord(operand)) return String(operand);
  if ('step' in operand) return context.t('pjFieldDiff.ruleStep', { n: operand['step'] });
  if ('literal' in operand) {
    const unit = operand['unit'] === undefined ? '' : nameOf(operand['unit'], context.labels);
    return `${String(operand['literal'])}${unit}`;
  }
  const term = nameOf(operand['term'], context.labels);
  const when = operand['when'];
  return isRecord(when)
    ? `${term}${context.t('pjFieldDiff.formulaWhen', {
        property: nameOf(when['property'], context.labels),
        value: nameOf(when['value'], context.labels),
      })}`
    : term;
}

/**
 * 式の段（名前 ＝ 項 ＋ 項）を文字にする。形が合わなければ null。
 * @graph-connects isRecord [calls] 段と式の形か
 * @graph-connects nameOf [calls] 段の名前の番号を名前に
 * @graph-connects operandText [calls] 項 1 つ
 */
function stepText(value: unknown, context: FormatContext): string | null {
  const expression = isRecord(value) ? value['expression'] : undefined;
  if (!isRecord(value) || !isRecord(expression) || !Array.isArray(expression['operands'])) {
    return null;
  }
  const operator = context.t(`businessLogic.formulaOperators.${String(expression['operator'])}`, {
    defaultValue: ` ${String(expression['operator'])} `,
  });
  return `${nameOf(value['name'], context.labels)}${context.t('pjFieldDiff.formulaEquals')}${expression[
    'operands'
  ]
    .map((operand: unknown) => operandText(operand, context))
    .join(operator)}`;
}

/**
 * 並べ替えのキーの行（優先 ・ 順 ・ 値が無いとき）を文字にする。形が合わなければ null。
 * @graph-connects isRecord [calls] キーの行の形か
 * @graph-connects nameOf [calls] 値の並びの番号を名前に
 */
function sortKeyText(value: unknown, context: FormatContext): string | null {
  if (!isRecord(value)) return null;
  const { order, rank, whenMissing } = value;
  const orderText = Array.isArray(order)
    ? order.map((entry: unknown) => nameOf(entry, context.labels)).join(' → ')
    : context.t(`businessLogic.sortDirections.${String(order)}`, { defaultValue: String(order) });
  return [
    context.t('pjFieldDiff.sortRank', { n: rank }),
    orderText,
    context.t('pjFieldDiff.sortWhenMissing', {
      value: context.t(`businessLogic.sortMissingValues.${String(whenMissing)}`, {
        defaultValue: String(whenMissing),
      }),
    }),
  ].join(' ・ ');
}

/** 語彙を引く欄（業務ルールの形・CRUD・重なりの扱い・同順位）の語の置き場。 @graph-connects none */
const RULE_WORD_KEYS: Readonly<Record<string, string>> = {
  kind: 'businessLogic.ruleKind',
  crud: 'businessLogic.crud',
  overlapPolicy: 'businessLogic.hitPolicies',
  tieBreak: 'businessLogic.sortTieBreaks',
};

/**
 * 欄の片側の値を画面の文字にする。値が無ければ null（描く側が空札にする）。
 *
 * 判定表の行は「条件 ・ 条件 → 出力」、式の段は「名前 ＝ 項 ＋ 項（条件 とき）」、並べ替えのキーの行は
 * 「優先 n ・ 順 ・ 値が無いとき」、業務ルールの形・CRUD・重なりの扱い・同順位は語、ほかは
 * `formatPjDiffValue`（番号は名前、配列は ／、入れ子は 欄: 値）で出す。**JSON のまま出す値は無い。**
 *
 * @param path 欄が指す場所
 * @param value 欄の片側の値
 * @param context 番号の名前・文言・表示言語・要素の型
 * @returns 表示する文字。値が無ければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の欄の片側の値を、判定表の行・式の段・並べ替えのキー・業務ルールの語・一般の値の形ごとに、番号を名前にした文字へ直す。
 * @graph-connects rowText [calls] 判定表の行
 * @graph-connects stepText [calls] 式の段
 * @graph-connects sortKeyText [calls] 並べ替えのキーの行
 * @graph-connects formatPjDiffValue [calls] 形を知らない値の文字化
 */
export function formatPjDiffFieldValue(
  path: PjDiffFieldPath,
  value: unknown,
  context: FormatContext,
): string | null {
  if (value === null || value === undefined) return null;
  const structured =
    path.part === 'row' || path.part === 'deriveRow'
      ? rowText(value, context)
      : path.part === 'step' || path.part === 'deriveStep'
        ? stepText(value, context)
        : path.part === 'sortKey'
          ? sortKeyText(value, context)
          : null;
  if (structured !== null) return structured;
  const wordKey =
    typeof value === 'string' &&
    (path.part === 'body' ||
      path.part === 'derive' ||
      (path.part === 'property' && context.nodeType === 'Rule'))
      ? RULE_WORD_KEYS[path.name]
      : undefined;
  if (wordKey !== undefined && typeof value === 'string') {
    return context.t(`${wordKey}.${value}`, { defaultValue: value });
  }
  return formatPjDiffValue(value, context.locale, context.labels);
}
